import { lazy } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { hasSession } from "./auth/session";
import LoginPage from "./pages/LoginPage";
import ConsoleLayout from "./components/templates/ConsoleLayout";

// 화면은 라우트마다 따로 내려받는다(React.lazy). 로그인과 틀만 첫 묶음에 싣고, Suspense 는
// ConsoleLayout 의 Outlet 자리에 하나 — 틀은 그대로 두고 본문만 바뀐다.
const EventLogsPage = lazy(() => import("./pages/EventLogsPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const MonitoringPage = lazy(() => import("./pages/MonitoringPage"));
const StationsPage = lazy(() => import("./pages/StationsPage"));
const StationFormPage = lazy(() => import("./pages/StationFormPage"));
const SetupRequestsPage = lazy(() => import("./pages/SetupRequestsPage"));
const SetupRequestDetailPage = lazy(() => import("./pages/SetupRequestDetailPage"));
const StationDetailPage = lazy(() => import("./pages/StationDetailPage"));
const ChargersPage = lazy(() => import("./pages/ChargersPage"));
const ChargerFormPage = lazy(() => import("./pages/ChargerFormPage"));
const ChargerDetailPage = lazy(() => import("./pages/ChargerDetailPage"));
const MembersPage = lazy(() => import("./pages/MembersPage"));
const MemberNewPage = lazy(() => import("./pages/MemberNewPage"));
const MemberDetailPage = lazy(() => import("./pages/MemberDetailPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const SalesSettlementPage = lazy(() => import("./pages/SalesSettlementPage"));
const StationPricePage = lazy(() => import("./pages/StationPricePage"));
const PromotionsPage = lazy(() => import("./pages/PromotionsPage"));
const PromotionFormPage = lazy(() => import("./pages/PromotionFormPage"));
const FaultsPage = lazy(() => import("./pages/FaultsPage"));
const CsPage = lazy(() => import("./pages/CsPage"));
const CsDetailPage = lazy(() => import("./pages/CsDetailPage"));
const HistoryPage = lazy(() => import("./pages/HistoryPage"));
const HistoryDetailPage = lazy(() => import("./pages/HistoryDetailPage"));
const CorpPage = lazy(() => import("./pages/CorpPage"));
const CorpDetailPage = lazy(() => import("./pages/CorpDetailPage"));
const MakersPage = lazy(() => import("./pages/MakersPage"));
const ChargerModelFormPage = lazy(() => import("./pages/ChargerModelFormPage"));
const MakerFormPage = lazy(() => import("./pages/MakerFormPage"));
const ErrorCodesPage = lazy(() => import("./pages/ErrorCodesPage"));
const StipulationsPage = lazy(() => import("./pages/StipulationsPage"));
const StipulationHistoryPage = lazy(() => import("./pages/StipulationHistoryPage"));
const StatsPage = lazy(() => import("./pages/StatsPage"));
const CertRecordsPage = lazy(() => import("./pages/CertRecordsPage"));
const AdminsPage = lazy(() => import("./pages/AdminsPage"));
const AdminDetailPage = lazy(() => import("./pages/AdminDetailPage"));
const UserCardsPage = lazy(() => import("./pages/UserCardsPage"));
const CardStockPage = lazy(() => import("./pages/CardStockPage"));
const UserCardDetailPage = lazy(() => import("./pages/UserCardDetailPage"));
const NoticeDetailPage = lazy(() => import("./pages/NoticeDetailPage"));
const TossSettlementPage = lazy(() => import("./pages/TossSettlementPage"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const FaqDetailPage = lazy(() => import("./pages/FaqDetailPage"));
const CarModelsPage = lazy(() => import("./pages/CarModelsPage"));
const TelegramsPage = lazy(() => import("./pages/TelegramsPage"));
const RemoteControlsPage = lazy(() => import("./pages/RemoteControlsPage"));
const RoamingPage = lazy(() => import("./pages/RoamingPage"));
const SettlementPage = lazy(() => import("./pages/SettlementPage"));
const UnpaidMembersPage = lazy(() => import("./pages/UnpaidMembersPage"));
const AsArticlesPage = lazy(() => import("./pages/AsArticlesPage"));
const AsDetailPage = lazy(() => import("./pages/AsDetailPage"));
const PopupsPage = lazy(() => import("./pages/PopupsPage"));
const NoticesPage = lazy(() => import("./pages/NoticesPage"));
const PriceGuidePage = lazy(() => import("./pages/PriceGuidePage"));
const AptSettlementsPage = lazy(() => import("./pages/AptSettlementsPage"));
const AptSettlementDetailPage = lazy(() => import("./pages/AptSettlementDetailPage"));
const RoamingSettlementPage = lazy(() => import("./pages/RoamingSettlementPage"));
const MemberActivityPage = lazy(() => import("./pages/MemberActivityPage"));
const UnitHistPage = lazy(() => import("./pages/UnitHistPage"));
const ApartmentsPage = lazy(() => import("./pages/ApartmentsPage"));
const ApartmentDetailPage = lazy(() => import("./pages/ApartmentDetailPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const SettlementSummaryPage = lazy(() => import("./pages/SettlementSummaryPage"));
const PointsPage = lazy(() => import("./pages/PointsPage"));
const CouponsPage = lazy(() => import("./pages/CouponsPage"));
const HelpPage = lazy(() => import("./pages/HelpPage"));

/** 로그인 표시가 없으면 로그인으로. 진짜 판단은 쿠키를 든 서버가 하고, 표시가 낡았으면
 * 첫 요청이 401 로 답해 인터셉터가 로그인으로 보낸다. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  return hasSession() ? <>{children}</> : <Navigate to="/login" replace />;
}

// 옛 평평한 경로(/cs, /stations/…)를 그룹 경로로 승격해 북마크를 살린다(2026-09-14 라우팅 개편).
// LegacyRedirect: 하위 경로(splat)를 그대로 이어 붙인다 — /stations/UCH1/edit → /infra/stations/UCH1/edit.
function LegacyRedirect({ to }: { to: string }) {
  const rest = useParams()["*"];
  const { search } = useLocation();
  return <Navigate to={`${to}${rest ? `/${rest}` : ""}${search}`} replace />;
}
// 그룹 접두사와 겹치는 옛 상세경로(/history/:id, /members/:id)용 — 이름 파라미터 하나를 이어 붙인다.
function LegacyParam({ to }: { to: string }) {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`${to}${id ? `/${id}` : ""}${search}`} replace />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <ConsoleLayout />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />

        {/* 충전인프라 /infra */}
        <Route path="infra/monitoring" element={<MonitoringPage />} />
        <Route path="infra/stations" element={<StationsPage />} />
        <Route path="infra/stations/new" element={<StationFormPage />} />
        <Route path="infra/stations/:id/edit" element={<StationFormPage />} />
        <Route path="infra/stations/:id" element={<StationDetailPage />} />
        <Route path="infra/chargers" element={<ChargersPage />} />
        <Route path="infra/chargers/new" element={<ChargerFormPage />} />
        <Route path="infra/chargers/:id/edit" element={<ChargerFormPage />} />
        <Route path="infra/chargers/:id" element={<ChargerDetailPage />} />
        <Route path="infra/makers" element={<MakersPage />} />
        {/* 모델은 서랍이 아니라 주소가 있는 화면이다 — 새로고침해도 남고 링크로 건넬 수 있다. */}
        <Route path="infra/makers/companies/new" element={<MakerFormPage />} />
        <Route path="infra/makers/companies/:idntfr" element={<MakerFormPage />} />
        <Route path="infra/makers/models/new" element={<ChargerModelFormPage />} />
        <Route path="infra/makers/models/:idntfr" element={<ChargerModelFormPage />} />
        <Route path="infra/error-codes" element={<ErrorCodesPage />} />

        {/* 이력 /history */}
        <Route path="history/charges" element={<HistoryPage />} />
        <Route path="history/roaming" element={<HistoryPage roaming />} />
        <Route path="history/charges/:id" element={<HistoryDetailPage />} />
        <Route path="history/cs" element={<CsPage />} />
        <Route path="history/cs/:id" element={<CsDetailPage />} />
        <Route path="history/as" element={<AsArticlesPage />} />
        <Route path="history/as/:id" element={<AsDetailPage />} />
        <Route path="history/faults" element={<FaultsPage />} />
        <Route path="history/cert-records" element={<CertRecordsPage />} />
        <Route path="history/remote-controls" element={<RemoteControlsPage />} />
        <Route path="history/telegrams" element={<TelegramsPage />} />

        {/* 통계 */}
        <Route path="stats" element={<StatsPage />} />

        {/* 정산 /settlement */}
        <Route path="settlement/sales" element={<SalesSettlementPage />} />
        <Route path="settlement/charges" element={<TossSettlementPage />} />
        <Route path="settlement/corp" element={<SettlementPage />} />
        <Route path="settlement/summary" element={<SettlementSummaryPage />} />
        <Route path="settlement/roaming" element={<RoamingSettlementPage />} />
        <Route path="settlement/unpaid" element={<UnpaidMembersPage />} />
        <Route path="settlement/apartments" element={<AptSettlementsPage />} />
        <Route path="settlement/apartments/:apartmentId" element={<AptSettlementDetailPage />} />

        {/* 회원 관리 /members */}
        <Route path="members/list" element={<MembersPage />} />
        <Route path="members/list/new" element={<MemberNewPage />} />
        <Route path="members/list/:id" element={<MemberDetailPage />} />
        <Route path="members/apartments" element={<ApartmentsPage />} />
        <Route path="members/apartments/:code" element={<ApartmentDetailPage />} />
        <Route path="members/cards" element={<UserCardsPage />} />
        <Route path="members/cards/:id" element={<UserCardDetailPage />} />
        <Route path="members/card-stock" element={<CardStockPage />} />
        <Route path="members/activity" element={<MemberActivityPage />} />
        <Route path="members/unit-hist" element={<UnitHistPage />} />

        {/* 포인트/쿠폰 /rewards */}
        <Route path="rewards/points" element={<PointsPage />} />
        <Route path="rewards/coupons" element={<CouponsPage />} />

        {/* 운영 관리 /ops */}
        <Route path="ops/corp" element={<CorpPage />} />
        <Route path="ops/corp/:id" element={<CorpDetailPage />} />
        <Route path="ops/pricing" element={<PricingPage />} />
        <Route path="ops/pricing/stations/:idntfr" element={<StationPricePage />} />
        <Route path="ops/promotions" element={<PromotionsPage />} />
        <Route path="ops/promotions/new" element={<PromotionFormPage />} />
        <Route path="ops/promotions/:id" element={<PromotionFormPage />} />
        <Route path="ops/car-models" element={<CarModelsPage />} />
        <Route path="ops/admins" element={<AdminsPage />} />
        <Route path="ops/admins/:id" element={<AdminDetailPage />} />
        <Route path="ops/event-logs" element={<EventLogsPage />} />
        <Route path="ops/setup-requests" element={<SetupRequestsPage />} />
        <Route path="ops/setup-requests/:id" element={<SetupRequestDetailPage />} />

        {/* 로밍(타사충전·로밍요금제 공용) — 그룹 모호로 접두사 없이 유지 */}
        <Route path="roaming" element={<RoamingPage />} />

        {/* 홈페이지 관리 /site */}
        <Route path="site/notices" element={<NoticesPage />} />
        <Route path="site/notices/:id" element={<NoticeDetailPage />} />
        <Route path="site/faq" element={<FaqPage />} />
        <Route path="site/faq/:id" element={<FaqDetailPage />} />
        <Route path="site/stipulations" element={<StipulationsPage />} />
        <Route path="site/stipulations/:name" element={<StipulationHistoryPage />} />
        <Route path="site/popups" element={<PopupsPage />} />
        <Route path="site/price-guide" element={<PriceGuidePage />} />

        {/* 설정 /settings */}
        <Route path="settings/account" element={<SettingsPage />} />
        <Route path="settings/help" element={<HelpPage />} />

        {/* ── 옛 평평한 경로 → 새 그룹 경로(북마크 보존·URL 승격) ── */}
        <Route path="monitoring" element={<LegacyRedirect to="/infra/monitoring" />} />
        <Route path="stations/*" element={<LegacyRedirect to="/infra/stations" />} />
        <Route path="chargers/*" element={<LegacyRedirect to="/infra/chargers" />} />
        <Route path="makers" element={<LegacyRedirect to="/infra/makers" />} />
        <Route path="error-codes" element={<LegacyRedirect to="/infra/error-codes" />} />
        <Route path="history" element={<Navigate to="/history/charges" replace />} />
        <Route path="history/:id" element={<LegacyParam to="/history/charges" />} />
        <Route path="cs/*" element={<LegacyRedirect to="/history/cs" />} />
        <Route path="as-articles/*" element={<LegacyRedirect to="/history/as" />} />
        <Route path="faults" element={<LegacyRedirect to="/history/faults" />} />
        <Route path="cert-records" element={<LegacyRedirect to="/history/cert-records" />} />
        <Route path="remote-controls" element={<LegacyRedirect to="/history/remote-controls" />} />
        <Route path="telegrams" element={<LegacyRedirect to="/history/telegrams" />} />
        <Route path="settlement" element={<Navigate to="/settlement/sales" replace />} />
        <Route path="settlement-summary" element={<LegacyRedirect to="/settlement/summary" />} />
        <Route path="roaming-settlement" element={<LegacyRedirect to="/settlement/roaming" />} />
        <Route path="apt-settlements" element={<LegacyRedirect to="/settlement/apartments" />} />
        <Route path="members" element={<Navigate to="/members/list" replace />} />
        <Route path="members/new" element={<Navigate to="/members/list/new" replace />} />
        <Route path="members/:id" element={<LegacyParam to="/members/list" />} />
        <Route path="apartments" element={<LegacyRedirect to="/members/apartments" />} />
        <Route path="user-cards/*" element={<LegacyRedirect to="/members/cards" />} />
        <Route path="card-stock" element={<LegacyRedirect to="/members/card-stock" />} />
        <Route path="member-activity" element={<LegacyRedirect to="/members/activity" />} />
        <Route path="points" element={<LegacyRedirect to="/rewards/points" />} />
        <Route path="coupons" element={<LegacyRedirect to="/rewards/coupons" />} />
        <Route path="corp" element={<LegacyRedirect to="/ops/corp" />} />
        <Route path="pricing" element={<LegacyRedirect to="/ops/pricing" />} />
        <Route path="car-models" element={<LegacyRedirect to="/ops/car-models" />} />
        <Route path="admins" element={<LegacyRedirect to="/ops/admins" />} />
        <Route path="setup-requests" element={<LegacyRedirect to="/ops/setup-requests" />} />
        <Route path="notices" element={<LegacyRedirect to="/site/notices" />} />
        <Route path="faq" element={<LegacyRedirect to="/site/faq" />} />
        <Route path="stipulations" element={<LegacyRedirect to="/site/stipulations" />} />
        <Route path="popups" element={<LegacyRedirect to="/site/popups" />} />
        <Route path="settings" element={<Navigate to="/settings/account" replace />} />
        <Route path="help" element={<LegacyRedirect to="/settings/help" />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
